import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Loader2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import nooeLogo from '../assets/nooe-logo.jpeg';

export default function LoginPage() {
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setFormError(null);
    try {
      await login(email, password);
    } catch (err: any) {
      setFormError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen w-full flex flex-col md:flex-row font-sans select-none antialiased" data-testid="login-page-root">
      {/* LEFT: brand panel */}
      <div
        data-testid="login-brand-panel"
        className="hidden md:flex md:w-1/2 items-center justify-center relative overflow-hidden"
        style={{ backgroundColor: '#191714' }}
      >
        <div className="absolute top-[-15%] left-[-15%] w-[55%] h-[55%] rounded-full bg-white/[0.03] blur-[100px] pointer-events-none" />
        <div className="absolute bottom-[-15%] right-[-10%] w-[45%] h-[45%] rounded-full bg-white/[0.03] blur-[100px] pointer-events-none" />
        <motion.img
          data-testid="login-brand-logo"
          src={nooeLogo}
          alt="NOOE"
          initial={{ opacity: 0, scale: 0.92 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.9, ease: 'easeOut' }}
          className="w-48 h-48 lg:w-64 lg:h-64 object-contain rounded-3xl relative z-10"
        />
      </div>

      {/* RIGHT: sign-in panel */}
      <div
        className="w-full md:w-1/2 flex items-center justify-center p-6 md:p-12 relative overflow-hidden"
        style={{ background: 'linear-gradient(180deg, #F7EFE3 0%, #F3E8D8 100%)' }}
      >
        <div className="absolute top-[-10%] right-[-10%] w-[45%] h-[45%] rounded-full bg-blue-100/40 blur-[130px] pointer-events-none" />
        <div className="absolute bottom-[-10%] left-[-10%] w-[45%] h-[45%] rounded-full bg-sky-100/50 blur-[130px] pointer-events-none" />

        <div className="flex flex-col items-center text-center z-10 max-w-md w-full px-4 space-y-8">
          <img
            src={nooeLogo}
            alt="NOOE"
            className="md:hidden w-16 h-16 object-contain rounded-2xl"
          />

          <motion.h1
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="text-3xl md:text-4xl tracking-tight text-slate-900 leading-none font-medium"
            style={{ fontFamily: 'Georgia, serif' }}
          >
            Internal for NOOE
          </motion.h1>

          <motion.form
            data-testid="login-form"
            onSubmit={handleSubmit}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.15, ease: 'easeOut' }}
            className="w-full bg-white border border-[#e8dcc9] rounded-3xl shadow-[0_20px_50px_-15px_rgba(15,23,42,0.15)] p-8 space-y-5 text-left"
          >
            <div className="space-y-1.5">
              <label className="block text-[10px] uppercase font-mono tracking-wider font-bold text-slate-500">
                Work Email
              </label>
              <input
                data-testid="login-email-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@nooe.co"
                className="w-full bg-slate-50 border border-slate-200 px-4 py-2.5 text-sm rounded-xl focus:outline-none focus:border-slate-400 font-medium text-slate-800"
              />
            </div>
            <div className="space-y-1.5">
              <label className="block text-[10px] uppercase font-mono tracking-wider font-bold text-slate-500">
                Password
              </label>
              <input
                data-testid="login-password-input"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="w-full bg-slate-50 border border-slate-200 px-4 py-2.5 text-sm rounded-xl focus:outline-none focus:border-slate-400 font-medium text-slate-800"
              />
            </div>

            {formError && (
              <div
                data-testid="login-error-message"
                className="p-3 bg-rose-50 border border-rose-100 rounded-xl text-rose-700 text-xs font-medium"
              >
                {formError}
              </div>
            )}

            <button
              data-testid="login-submit-button"
              type="submit"
              disabled={submitting}
              className="group w-full flex items-center justify-center gap-3 bg-[#1d4ed8] text-white font-medium text-xs uppercase tracking-widest px-10 py-4 rounded-full shadow-lg shadow-blue-900/10 hover:shadow-xl hover:shadow-blue-900/20 hover:scale-[1.02] active:scale-98 transition-all duration-300 border border-blue-800/30 cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {submitting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  <span>Sign In</span>
                  <ArrowRight className="h-4 w-4 text-blue-200 group-hover:translate-x-1.5 transition-transform duration-300" />
                </>
              )}
            </button>

            <p className="text-[10px] text-slate-400 text-center pt-1">
              No public signup. Access is granted only via admin invitation.
            </p>
          </motion.form>
        </div>
      </div>
    </div>
  );
}
